import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CandidateCard from "../../components/candidates/condensed_card_application.js";
import ApplicationsFeedback from "../../components/feedback/ApplicationsFeedback.js";
import RolesOverview from "../../components/roles/RolesOverview.js";

import {
  RolesLoading,
  CandidateCardLoading,
} from "../../components/loading/loading_interviews.js";

export default function Applications() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState();
  const [filter, setFilter] = useState("");
  const [roles, setRoles] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [activeRole, setActiveRole] = useState("");
  const [interviewNeedUpdate, setInterviewNeedUpdate] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [showFinalPopup, setShowFinalPopup] = useState(false);
  const [candidatesRequest, setCandidatesRequest] = useState(0);
  const [isRequested, setIsRequested] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const [showReminderBanner, setShowReminderBanner] = useState(true);
  const [selectedRoleForReopen, setSelectedRoleForReopen] = useState(null);
  const [showReopenDropdown, setShowReopenDropdown] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/passwordless-login");
    }
  }, [navigate]);

  // Fetch current user information
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.log("No token found in localStorage");
          return;
        }
        
        console.log("Fetching current user in Applications...");
        const { data } = await axios.get("/api/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Current user fetched in Applications:", data.user);
        setCurrentUser(data.user);
      } catch (err) {
        console.error("Failed to fetch current user in Applications", err);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (!interviewNeedUpdate) return;
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get("/api/roles/fetchRoles", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const roles = response.data.roles || [];
        const transformedData = roles.map((role) => {
          // Check if role is publishable based on required fields
          const mandatoryFields = [
            role["Role Title"],
            role["Compensation"],
            role["Working Style"],
            role["Hiring Lead Name"], 
            role["Hiring Lead Email Address"],
          ];
          
          const importantFields = [
            role["Job Description PDF"], 
            role["Spotlight"]
          ];
          
          const filledMandatoryFields = mandatoryFields.filter(Boolean).length;
          const filledImportantFields = importantFields.filter(Boolean).length;
          
          // A role is publishable if all mandatory fields are filled AND at least one important field is filled
          const isPublishable = 
            filledMandatoryFields === mandatoryFields.length && 
            filledImportantFields > 0;
            
          return {
            id: role.id,
            title: role["Role Title"],
            status: role["Open to applications"] === "Open to apps" ? true : false,
            isPublishable: isPublishable
          };
        });

        setRoles(transformedData);
        setLoadingRoles(false);
      } catch (err) {
        setError(err.message);
        setLoadingRoles(false);
      }
    };

    const transformRoles = (roles) => {
      const roleSet = new Set(roles);
      if (roleSet.size === 1) {
        if (roleSet.has("Full Stack Developer")) return ["Full Stack"];
        if (roleSet.has("Front End Developer")) return ["Front End"];
        if (roleSet.has("Back End Developer")) return ["Back End"];
      }
      if (
        roleSet.has("Front End Developer") &&
        roleSet.has("Back End Developer")
      ) {
        return ["Full Stack"];
      }
      if (roleSet.has("Full Stack Developer")) {
        return ["Full Stack"];
      }
      return roles;
    };

    const fetchInterviews = async () => {
      try {
        const token = localStorage.getItem("token");
        const stage = "Application pending"; // The stage to display here
        const response = await axios.get("/api/interviews/fetchInterviews", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { stage: encodeURIComponent(stage) },
        });

        const interviews = response.data.interviews || [];
        console.log("Raw response:", response);

        const transformedCandidate = interviews
          .map((interview) => {
            // If "Name (from Candidate)" is missing or empty, skip gracefully
            const nameFields = interview.fields?.["Name (from Candidate)"];
            if (!nameFields || nameFields.length === 0) {
              // No candidate data. Return null so we can filter it out.
              console.log(
                `Skipping interview [id=${interview.id}] due to empty candidate data.`
              );
              return null;
            }

            const stream =
              interview.fields?.["Stream (from CRM) (from Candidate)"]?.[0];

            let sweRoles = interview.fields["SWE relevant roles (from Candidate)"];
            if (sweRoles) {
              sweRoles = Array.isArray(sweRoles)
                ? sweRoles[0].split(",").map((role) => role.trim())
                : sweRoles.split(",").map((role) => role.trim());
              sweRoles = transformRoles(sweRoles);
            } else {
              sweRoles = [];
            }

            return {
              role: Array.isArray(interview.fields?.["Role"])
                ? interview.fields["Role"][0]
                : undefined,
              ranking: interview.fields?.["Ranking"] || undefined,
              role_title: Array.isArray(
                interview.fields?.["Role Title (from Role)"]
              )
                ? interview.fields["Role Title (from Role)"][0]
                : undefined,
              interviewId: interview.fields?.["id"] || undefined,
              url_id: interview.fields?.["ID (from Candidate)"] || undefined,
              candidate_record_id: interview.fields?.["Candidate"] ? interview.fields["Candidate"][0] : undefined,
              stage: interview.fields?.["Stage"] || undefined,
              isRecommended: interview.fields?.["JS Recommended Applicant"] || false,

              // LinkedIn
              linkedin: Array.isArray(
                interview.fields?.["LinkedIn RAW (from Candidate)"]
              )
                ? interview.fields["LinkedIn RAW (from Candidate)"][0]
                : undefined,

              // CV
              cv: Array.isArray(interview.fields?.["CV RAW (from Candidate)"])
                ? interview.fields["CV RAW (from Candidate)"][0]
                : undefined,

              // Candidate name
              name: Array.isArray(interview.fields?.["Name (from Candidate)"])
                ? interview.fields["Name (from Candidate)"][0]
                : undefined,
              lastName: Array.isArray(
                interview.fields?.["Surname (from Candidate)"]
              )
                ? interview.fields["Surname (from Candidate)"][0]
                : undefined,

              // Video
              video: Array.isArray(
                interview.fields?.["Video intro (new) From (ALL CANDIDATE PROFILES)"]
              )
                ? interview.fields["Video intro (new) From (ALL CANDIDATE PROFILES)"][0]
                : undefined,

              // Education
              undergrad_degree_type:
                interview.fields?.["Undergrad degree type (from Candidate)"] ||
                "",
              undergrad_degree_subject:
                interview.fields?.["Undergrad subject (from Candidate)"] || "",
              undergrad_university:
                interview.fields?.["Undergrad university (from Candidate)"] ||
                "",
              undergrad_graduation_year:
                interview.fields?.[
                  "Undergrad graduation year (from Candidate)"
                ] || "",
              undergrad_result:
                interview.fields?.["Undergrad result (from Candidate)"] || "",
              postgrad_degree_type:
                interview.fields?.["Postgrad degree type (from Candidate)"] ||
                "",
              postgrad_degree_subject:
                interview.fields?.["Postgrad subject (from Candidate)"] || "",
              postgrad_university:
                interview.fields?.["Postgrad university (from Candidate)"] ||
                "",
              postgrad_graduation_year:
                interview.fields?.[
                  "Postgrad graduation year (from Candidate)"
                ] || "",
              postgrad_result:
                interview.fields?.["Postgrad result (from Candidate)"] || "",
              roles:
                interview.fields?.[
                  "Generalist roles suitable for copy (from Candidate)"
                ] || undefined,
              email: Array.isArray(interview.fields?.["Email (from Candidate)"])
                ? interview.fields["Email (from Candidate)"][0]
                : undefined,
              working_style:
                interview.fields?.[
                  "Preferred working style copy (from Candidate)"
                ] || undefined,
              image:
                interview.fields?.["Photo (from Candidate)"]?.[0]?.url ||
                undefined,
              work_experience_1_role:
                interview.fields?.[
                  "(1) Work Experience Role (from Candidate)"
                ] || "",
              work_experience_1_company:
                interview.fields?.[
                  "(1) Work Experience Company (from Candidate)"
                ] || "",
              work_experience_1_description:
                interview.fields?.[
                  "(1) Work Experience Description (from Candidate)"
                ] || "",
              work_experience_1_duration: {
                years: interview.fields?.["(1) Time spent in work experience YRS NEW (from Candidate)"]?.[0] || 0,
                months: interview.fields?.["(1) Time spent in work experience MOS NEW (from Candidate)"]?.[0] || 0,
              },
              work_experience_2_role:
                interview.fields?.[
                  "(2) Work Experience Role (from Candidate)"
                ] || "",
              work_experience_2_company:
                interview.fields?.[
                  "(2) Work Experience Company (from Candidate)"
                ] || "",
              work_experience_2_description:
                interview.fields?.[
                  "(2) Work Experience Description (from Candidate)"
                ] || "",
              work_experience_2_duration: {
                 years: interview.fields?.["(2) Time spent in work experience YRS NEW (from Candidate)"]?.[0] || 0,
                 months: interview.fields?.["(2) Time spent in work experience MOS NEW (from Candidate)"]?.[0] || 0,
              },
              languages:
                interview.fields?.["Languages copy (from Candidate)"] ||
                undefined,
              description:
                interview.fields?.["Top achievement (from Candidate)"] ||
                undefined,
              mostRecentCohort: Array.isArray(
                interview.fields?.["Most recent cohort (from Candidate)"]
              )
                ? interview.fields["Most recent cohort (from Candidate)"][0]
                : undefined,
              recentTarget:
                interview.fields?.["Recent target achieved (from Candidate)"] ||
                undefined,
              stream: stream || undefined,
              rationale:
                interview.fields?.["Application Rationale"] || undefined,
              sales:
                interview.fields?.[
                  "Proven Sales Experience / High Potential for Sales (from Candidate)"
                ] || undefined,
              github: Array.isArray(
                interview.fields?.["Github RAW (from Candidate)"]
              )
                ? interview.fields["Github RAW (from Candidate)"][0]
                : "",
              exp_swe:
                interview.fields?.["Level of exp SWE (from Candidate)"] || "",
              portfolio: Array.isArray(
                interview.fields?.["Raw Portfolio (from Candidate)"]
              )
                ? interview.fields["Raw Portfolio (from Candidate)"][0]
                : "",
              time_coding:
                interview.fields?.["Time spent coding (from Candidate)"] || "",
              coding_languages: Array.isArray(
                interview.fields?.["Coding languages new (from Candidate)"]
              )
                ? interview.fields["Coding languages new (from Candidate)"] ||
                  ""
                : "",

              swe_roles: sweRoles,
              minimum_salary:
                interview.fields?.["Minimum salary (from Candidate)"] || "",
              ideal_salary:
                interview.fields?.["Ideal salary (from Candidate)"] || "",
              created: interview.fields?.["Created"] || undefined,
              right_to_work:
                interview.fields?.["RTW doc (from Candidate)"] || "",
            };
          })
          .filter(Boolean); // Remove any nulls from missing candidate data

        // Remove duplicates: Keep only the most recent application for each unique candidate+role combination
        const uniqueCandidates = transformedCandidate.reduce((acc, candidate) => {
          // Use candidate_record_id as the primary identifier, fallback to email or url_id
          const candidateId = candidate.candidate_record_id || candidate.email || candidate.url_id;
          const roleId = candidate.role || candidate.role_title;
          
          if (!candidateId) {
            // If no unique identifier is available, include the candidate anyway
            console.log(`No unique ID found for candidate ${candidate.name}, including anyway`);
            acc.push(candidate);
            return acc;
          }

          // Create a composite key for candidate + role to detect duplicates for the same role only
          const compositeKey = `${candidateId}|${roleId}`;

          const existingCandidate = acc.find(c => {
            const existingCandidateId = c.candidate_record_id || c.email || c.url_id;
            const existingRoleId = c.role || c.role_title;
            const existingCompositeKey = `${existingCandidateId}|${existingRoleId}`;
            return existingCompositeKey === compositeKey;
          });

          if (!existingCandidate) {
            // First occurrence of this candidate for this role
            console.log(`Adding candidate ${candidate.name} (${candidateId}) for role ${candidate.role_title} - first occurrence`);
            acc.push(candidate);
          } else {
            // Duplicate found for the same candidate and role - keep the most recent one
            const candidateDate = new Date(candidate.created || 0);
            const existingDate = new Date(existingCandidate.created || 0);
            
            console.log(`Duplicate found for ${candidate.name} (${candidateId}) applying to ${candidate.role_title}:
              Current: ${candidate.created} (${candidateDate.getTime()})
              Existing: ${existingCandidate.created} (${existingDate.getTime()})`);
            
            if (candidateDate > existingDate) {
              // Replace with more recent application
              const index = acc.indexOf(existingCandidate);
              acc[index] = candidate;
              console.log(`✓ Replaced with more recent application for ${candidate.role_title}`);
            } else {
              console.log(`✗ Keeping existing more recent application for ${existingCandidate.role_title}`);
            }
          }

          return acc;
        }, []);

        console.log(`Deduplication complete: ${transformedCandidate.length} → ${uniqueCandidates.length} candidates`);

        console.log("Transformed candidates:", transformedCandidate);
        console.log("Unique candidates after deduplication:", uniqueCandidates);
        setCandidates(uniqueCandidates);

        const requestCount = uniqueCandidates.filter(
          (candidate) => candidate.stage === "Application pending"
        ).length;
        setCandidatesRequest(requestCount);

        setLoadingCandidates(false);
      } catch (err) {
        console.error("Error fetching interviews:", err);
        if (
          err.response &&
          err.response.data &&
          err.response.data.message === "Internal Server Error" &&
          err.response.data.error ===
            "Error: No interviews found for this company"
        ) {
          setCandidates([]); // Set empty state if no interviews found
        } else {
          setError(err.message);
        }
        setLoadingCandidates(false);
      }
    };

    fetchRoles();
    fetchInterviews();
    setInterviewNeedUpdate(false);
  }, [interviewNeedUpdate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showReopenDropdown && !event.target.closest('.relative')) {
        setShowReopenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showReopenDropdown]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  // Sorting function
  const sortCandidates = (candidatesList) => {
    // Helper function to check if candidate is recommended
    const isRecommendedCandidate = (candidate) => {
      return candidate.isRecommended;
    };

    // Base sort: Always prioritize recommended candidates, then sort by date (newest first)
    const baseSortWithRecommended = (a, b) => {
      const aIsRecommended = isRecommendedCandidate(a);
      const bIsRecommended = isRecommendedCandidate(b);
      
      // First priority: Recommended candidates at top
      if (aIsRecommended && !bIsRecommended) return -1;
      if (!aIsRecommended && bIsRecommended) return 1;
      
      // Second priority: Sort by date (newest first)
      if (!a.created) return 1;
      if (!b.created) return -1;
      return new Date(b.created) - new Date(a.created);
    };

    if (sortOrder === 1) {
      // Thumbs up at the top (but recommended still takes precedence)
      return [...candidatesList].sort((a, b) => {
        const aIsRecommended = isRecommendedCandidate(a);
        const bIsRecommended = isRecommendedCandidate(b);
        
        // First priority: Recommended candidates
        if (aIsRecommended && !bIsRecommended) return -1;
        if (!aIsRecommended && bIsRecommended) return 1;
        
        // Second priority: Ranking
        if (a.ranking === "Thumbs up" && b.ranking !== "Thumbs up") return -1;
        if (b.ranking === "Thumbs up" && a.ranking !== "Thumbs up") return 1;
        if (a.ranking === "Not ranked" && b.ranking !== "Not ranked") return -1;
        if (b.ranking === "Not ranked" && a.ranking !== "Not ranked") return 1;
        
        // Third priority: Date
        if (!a.created) return 1;
        if (!b.created) return -1;
        return new Date(b.created) - new Date(a.created);
      });
    } else if (sortOrder === 2) {
      // Thumbs down at the top (but recommended still takes precedence)
      return [...candidatesList].sort((a, b) => {
        const aIsRecommended = isRecommendedCandidate(a);
        const bIsRecommended = isRecommendedCandidate(b);
        
        // First priority: Recommended candidates
        if (aIsRecommended && !bIsRecommended) return -1;
        if (!aIsRecommended && bIsRecommended) return 1;
        
        // Second priority: Ranking
        if (a.ranking === "Thumbs down" && b.ranking !== "Thumbs down") return -1;
        if (b.ranking === "Thumbs down" && a.ranking !== "Thumbs down") return 1;
        if (a.ranking === "Not ranked" && b.ranking !== "Not ranked") return -1;
        if (b.ranking === "Not ranked" && a.ranking !== "Not ranked") return 1;
        
        // Third priority: Date
        if (!a.created) return 1;
        if (!b.created) return -1;
        return new Date(b.created) - new Date(a.created);
      });
    } else if (sortOrder === 3) {
      // Not ranked at the top (but recommended still takes precedence)
      return [...candidatesList].sort((a, b) => {
        const aIsRecommended = isRecommendedCandidate(a);
        const bIsRecommended = isRecommendedCandidate(b);
        
        // First priority: Recommended candidates
        if (aIsRecommended && !bIsRecommended) return -1;
        if (!aIsRecommended && bIsRecommended) return 1;
        
        // Second priority: Ranking
        if (a.ranking === "Not ranked" && b.ranking !== "Not ranked") return -1;
        if (b.ranking === "Not ranked" && a.ranking !== "Not ranked") return 1;
        
        // Third priority: Date
        if (!a.created) return 1;
        if (!b.created) return -1;
        return new Date(b.created) - new Date(a.created);
      });
    } else if (sortOrder === 4) {
      // Newest to Oldest (recommended candidates still at top)
      return [...candidatesList].sort(baseSortWithRecommended);
    } else if (sortOrder === 5) {
      // Oldest to Newest (but recommended still at top, then oldest among non-recommended)
      return [...candidatesList].sort((a, b) => {
        const aIsRecommended = isRecommendedCandidate(a);
        const bIsRecommended = isRecommendedCandidate(b);
        
        // First priority: Recommended candidates at top
        if (aIsRecommended && !bIsRecommended) return -1;
        if (!aIsRecommended && bIsRecommended) return 1;
        
        // Second priority: Sort by date (oldest first among each group)
        if (!a.created) return 1;
        if (!b.created) return -1;
        return new Date(a.created) - new Date(b.created);
      });
    }
    // Default sorting (sortOrder === 0): Recommended first, then newest first
    return [...candidatesList].sort(baseSortWithRecommended);
  };

  // Filter & sort
  const filteredData = candidates.filter((candidate) => {
    if (filter && candidate.stage !== filter) return false;
    // Guard against undefined role_title
    if (activeRole && candidate.role_title && candidate.role_title !== activeRole)
      return false;
    return true;
  });
  const sortedData = sortCandidates(filteredData);

  // Count applicants by role
  const applicantsByRole = {};
  candidates.forEach(candidate => {
    if (candidate.role_title) {
      applicantsByRole[candidate.role_title] = (applicantsByRole[candidate.role_title] || 0) + 1;
    }
  });

  // Handle reopening a role
  const handleReopenRole = async (roleId) => {
    try {
      const token = localStorage.getItem("token");
      const updatedData = {
        fields: { "Open to applications": "Open to apps" },
        roleId: roleId,
      };
      
      await axios.patch("/api/roles/updateRoleData", updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Update the local state to reflect the change
      setRoles(prev => prev.map(role => 
        role.id === roleId 
          ? { ...role, status: true } 
          : role
      ));
      
      // Trigger a refresh of the data
      setInterviewNeedUpdate(true);
    } catch (err) {
      console.error("Error reopening role:", err);
      setError("Failed to reopen role");
    }
  };

  const confirmReopenRole = () => {
    if (selectedRoleForReopen) {
      handleReopenRole(selectedRoleForReopen.id);
      setSelectedRoleForReopen(null);
    }
  };

  const activeRolesCount = roles.filter(role => role.status).length;

  // Filter by role when clicking on role in sidebar
  const handleRoleFilter = (roleTitle) => {
    setActiveRole(roleTitle);
  };

  return (
    <>
      <div className="relative bg-white min-h-screen">
        {showReminderBanner && (
          <div className="hidden lg:block fixed top-0 w-full bg-[#02B491] border-b border-[#02a482] z-40">
            <div className="w-full px-4 py-3 flex items-center justify-center">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white mr-2 flex-shrink-0">
                  <path fillRule="evenodd" d="M5.25 9a6.75 6.75 0 0113.5 0v.75c0 2.123.8 4.057 2.118 5.52a.75.75 0 01-.297 1.206c-1.544.57-3.16.99-4.831 1.243a3.75 3.75 0 11-7.48 0 24.585 24.585 0 01-4.831-1.244.75.75 0 01-.298-1.205A8.217 8.217 0 005.25 9.75V9zm4.502 8.9a2.25 2.25 0 104.496 0 25.057 25.057 0 01-4.496 0z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-white font-bold">
                  Respond within 48hrs to maximize engagement. Applications expire after a period of time.
                </p>
              </div>
              <button 
                onClick={() => setShowReminderBanner(false)}
                className="text-white hover:text-gray-200 ml-4"
                aria-label="Dismiss banner"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        <div>
          <div className="flex lg:ml-[200px] h-full pb-20 justify-center bg-white">
            <div className="w-full max-w-[1500px]">
              <div className={`bg-white pl-4 lg:pl-12 px-4 pt-6 pb-4 fixed ${showReminderBanner ? 'top-[41px]' : 'top-18'} z-20 w-full border-b`}>
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center">
                  <div>
                    <h2 className="text-2xl lg:text-3xl">Applications</h2>
                    <div className="flex flex-col lg:flex-row lg:items-center mt-2 lg:mt-0">
                      <p className="mb-3 lg:mb-0 lg:mr-6 text-sm lg:text-md">
                        There are <span className="font-bold">{sortedData.length}</span> candidates that are waiting for a response from you.
                      </p>
                      <div className="flex flex-col lg:flex-row lg:items-center space-y-2 lg:space-y-0">
                        <div className="lg:mr-3">
                          <label htmlFor="sort-order" className="sr-only">Sort by</label>
                          <select
                            id="sort-order"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(parseInt(e.target.value, 10))}
                            className="border p-2 rounded-md text-sm w-full lg:w-36"
                          >
                            <option value={0}>Sort by Default</option>
                            <option value={4}>Newest First</option>
                            <option value={5}>Oldest First</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="role-filter" className="sr-only">Filter by role</label>
                          <select
                            id="role-filter"
                            value={activeRole}
                            onChange={(e) => setActiveRole(e.target.value)}
                            className="border p-2 rounded-md text-sm w-full lg:w-36"
                          >
                            <option value="">Filter by Role</option>
                            {roles.map((role) => (
                              <option key={role.id} value={role.title}>
                                {role.title} ({applicantsByRole[role.title] || 0})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={`flex flex-col lg:flex-row pt-[180px] lg:pt-44 ${candidates.length > 0 ? 'lg:pr-12' : 'justify-center'} px-4 lg:pl-4`}>
                {/* Main content with candidate cards or "No applications" message */}
                <div className={`flex-1 ${candidates.length > 0 ? 'lg:pr-10' : ''}`}>
                  {loadingCandidates ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 ml-2 lg:ml-4">
                      {Array(4)
                        .fill(0)
                        .map((_, index) => (
                          <CandidateCardLoading key={index} />
                        ))}
                    </div>
                  ) : candidates.length === 0 ? (
                    <div className="flex justify-center px-4">
                      <div className="mt-2 bg-white w-full max-w-xl rounded-lg p-4 lg:p-6 shadow-sm text-center">
                        <div className="flex justify-center mb-4">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-gray-400">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h2 className="text-lg lg:text-xl font-medium mb-2">
                          No active applications at the moment
                        </h2>
                        
                        {/* Show open roles section if any exist */}
                        {roles.filter(role => role.status).length > 0 && (
                          <div className="my-4 text-left w-full max-w-md mx-auto">
                            <h3 className="font-medium text-gray-700 mb-2 text-center text-sm lg:text-md">Waiting on applicants for roles:</h3>
                            <div className="max-h-60 overflow-y-auto flex flex-col items-center gap-2 p-1">
                              {roles
                                .filter(role => role.status)
                                .map(role => (
                                  <span
                                    key={role.id}
                                    className="bg-green-50 text-green-700 border border-green-100 rounded-md px-3 py-2 text-sm w-full text-center"
                                  >
                                    {role.title}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Show closed roles section if any exist to reopen */}
                        {roles.filter(role => !role.status && role.isPublishable).length > 0 && (
                          <div className="my-4 text-left w-full max-w-md mx-auto">
                            <h3 className="font-medium text-gray-700 mb-2 text-center text-sm lg:text-md">Available roles to reopen:</h3>
                            <div className="max-h-60 overflow-y-auto flex flex-col items-center gap-2 mb-3 p-1">
                              {roles
                                .filter(role => !role.status && role.isPublishable)
                                .map(role => (
                                  <button
                                    key={role.id}
                                    onClick={() => setSelectedRoleForReopen(role)}
                                    className="flex items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-md px-3 py-2 text-sm transition-colors w-full"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                    </svg>
                                    Reopen {role.title}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Call to action buttons */}
                        <div className="mt-4 flex flex-col lg:flex-row gap-2 lg:gap-0 justify-center">
                          <button
                            onClick={() => navigate("/roles")}
                            className="px-4 py-2 bg-[#02B491] text-white rounded-md hover:bg-[#02a082] transition-colors lg:mr-3 text-base font-medium w-full lg:w-auto"
                          >
                            Create a new role
                          </button>
                          { roles.filter(role => role.status).length === 0 &&
                            roles.filter(role => !role.status && role.isPublishable).length === 0 && (
                            <button
                              onClick={() => navigate("/browse-candidates")}
                              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-base font-medium w-full lg:w-auto"
                            >
                              Browse candidates
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 ml-2 lg:ml-4">
                      {sortedData.map((candidate, index) => (
                        <CandidateCard
                          key={index}
                          candidate={candidate}
                          onSelectCandidate={handleSelectCandidate}
                          setInterviewNeedUpdate={setInterviewNeedUpdate}
                          setShowPopup={setShowPopup}
                          setShowFinalPopup={setShowFinalPopup}
                          candidatesRequest={candidatesRequest}
                          setCandidatesRequest={setCandidatesRequest}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Side panel - Conditionally Rendered */}
                {candidates.length > 0 && (
                  <div className="w-full lg:w-64 mt-6 lg:mt-0">
                    {!loadingRoles ? (
                      <div className="lg:sticky lg:top-[170px] pt-0 mt-[17px]">
                        {/* Active Roles Section - Redesigned */}
                        <div className="mb-6 bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                <h4 className="text-base lg:text-lg font-medium text-gray-700">
                                  Active Roles
                                  {activeRolesCount > 0 && (
                                    <span className="ml-1 text-sm text-gray-500">
                                      ({activeRolesCount})
                                    </span>
                                  )}
                                </h4>
                              </div>
                            </div>
                            <p className="text-xs lg:text-sm text-gray-500 mt-1">Currently receiving applications</p>
                          </div>
                          
                          <div className="divide-y divide-gray-100">
                            {activeRolesCount > 0 ? (
                              roles
                                .filter(role => role.status)
                                .map(role => (
                                  <button
                                    key={role.id}
                                    onClick={() => handleRoleFilter(role.title)}
                                    className={`w-full text-left px-4 py-3 flex justify-between items-center group hover:bg-gray-50 transition-colors ${activeRole === role.title ? 'bg-gray-50' : ''}`}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <div className={`w-1 h-4 rounded-sm ${activeRole === role.title ? 'bg-[#02B491]' : 'bg-transparent'}`}></div>
                                      <span className={`text-sm ${activeRole === role.title ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                        {role.title}
                                      </span>
                                    </div>
                                    <div className="flex-shrink-0">
                                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap">
                                        {applicantsByRole[role.title] || 0} applicants
                                      </span>
                                    </div>
                                  </button>
                                ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-gray-500 italic">No active roles</div>
                            )}
                          </div>

                          {activeRole && (
                            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                              <button 
                                onClick={() => setActiveRole("")}
                                className="text-xs text-[#02B491] hover:text-[#02a482] flex items-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Clear selection
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Closed Roles Section - Redesigned */}
                        {roles.filter(role => !role.status && role.isPublishable).length > 0 && (
                          <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                              <div className="flex items-center">
                                <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                                <h4 className="font-medium text-gray-700 text-lg">
                                  Inactive Roles
                                  <span className="ml-1 text-sm text-gray-500">
                                    ({roles.filter(role => !role.status && role.isPublishable).length})
                                  </span>
                                </h4>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">Not receiving applications (can be reopened)</p>
                            </div>
                            
                            <div className="divide-y divide-gray-100">
                              {roles
                                .filter(role => !role.status && role.isPublishable)
                                .map(role => (
                                  <div key={role.id} className="px-4 py-3 flex justify-between items-center">
                                    <span className="text-sm text-gray-600 truncate pr-2">{role.title}</span>
                                    <button
                                      onClick={() => setSelectedRoleForReopen(role)}
                                      className="text-xs font-medium text-[#02B491] hover:text-[#02a482] hover:underline flex items-center flex-shrink-0"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                      </svg>
                                      Reopen
                                    </button>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="pt-2">
                        <RolesLoading />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {showFinalPopup && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative bg-white p-4 lg:p-6 py-8 lg:py-10 rounded-md shadow-md text-center mx-4 w-full max-w-lg">
              <button
                onClick={() => {
                  setShowFinalPopup(false);
                  setShowReopenDropdown(false);
                }}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-xl lg:text-2xl font-semibold mb-2">
                Thanks for responding to all your applications!
              </h2>
              <p className="text-sm lg:text-md">
                Want to keep the momentum going? Why not browse and request standout candidates or reopen a role to new applications.
              </p>
              <div className="mt-4 flex flex-col lg:flex-row gap-2 lg:space-x-2 justify-center">
                <button
                  onClick={() => {
                    setShowFinalPopup(false);
                    navigate("/browse-candidates");
                  }}
                  className="w-full lg:w-auto px-4 py-2 bg-[#02B491] text-white rounded-md hover:bg-white border-2 border-[#02B491] hover:text-black text-base font-medium"
                >
                  Browse candidates
                </button>
                <div className="relative w-full lg:w-auto">
                  <button
                    onClick={() => setShowReopenDropdown(!showReopenDropdown)}
                    disabled={roles.filter(role => !role.status && role.isPublishable).length === 0}
                    className={`w-full lg:w-auto px-4 py-2 rounded-md border-2 border-[#02B491] text-base font-medium flex items-center justify-center ${
                      roles.filter(role => !role.status && role.isPublishable).length === 0
                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                        : 'hover:bg-[#02B491] hover:text-white'
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    Reopen role
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>
                  {showReopenDropdown && roles.filter(role => !role.status && role.isPublishable).length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                      {roles
                        .filter(role => !role.status && role.isPublishable)
                        .map(role => (
                          <button
                            key={role.id}
                            onClick={() => {
                              setSelectedRoleForReopen(role);
                              setShowReopenDropdown(false);
                              setShowFinalPopup(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            {role.title}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {showPopup && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-[600px] py-8 lg:py-10 p-4 lg:p-6 rounded-md shadow-md text-center mx-4">
              <h2 className="text-xl lg:text-2xl font-semibold mb-2">Congrats!</h2>
              <p className="text-sm lg:text-md">You will now be introduced over email</p>
              <div className="mt-4">
                <button
                  onClick={() => setShowPopup(false)}
                  className="w-full lg:w-auto px-4 py-2 bg-[#02B491] text-white rounded-md text-base font-medium"
                >
                  Great!
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Reopen Role Confirmation Modal */}
        {selectedRoleForReopen && (
          <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>

            {/* Modal */}
            <div className="fixed inset-0 flex justify-center items-center z-50 px-4">
              <div className="relative w-full max-w-lg">
                <div className="relative bg-white rounded-lg shadow p-4 lg:p-6">
                  {/* Close button */}
                  <button
                    type="button"
                    onClick={() => setSelectedRoleForReopen(null)}
                    className="absolute top-2 right-2 text-gray-400 hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
                  >
                    <svg className="w-3 h-3" viewBox="0 0 14 14">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                      />
                    </svg>
                  </button>

                  <div className="p-4 lg:p-6 text-center">
                    {/* Icon */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="mx-auto mb-4 text-gray-400 w-10 h-10"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>

                    <h3 className="mb-2 text-lg lg:text-xl font-medium text-gray-800">
                      Reopen {selectedRoleForReopen.title}?
                    </h3>
                    <p className="text-sm text-gray-600 mb-5">
                      Once published, candidates can start applying straight away. The role will automatically close at midnight on Sunday.
                    </p>

                    <div className="flex flex-col lg:flex-row justify-center gap-2 lg:gap-3">
                      <button
                        onClick={confirmReopenRole}
                        type="button"
                        className="w-full lg:w-auto text-white bg-[#02B491] hover:bg-[#02a082] font-medium rounded-md text-base px-5 py-2.5"
                      >
                        Yes, reopen
                      </button>
                      <button
                        onClick={() => setSelectedRoleForReopen(null)}
                        type="button"
                        className="w-full lg:w-auto text-gray-500 bg-white hover:bg-gray-100 rounded-md border border-gray-200 text-base font-medium px-5 py-2.5 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
